import type { Locale } from '@/lib/content';
import type { ToolConfig } from './types';

export const tools: ToolConfig[] = [
  {
    id: 'pdf-to-ubl',
    slugs: {
      nl: 'pdf-naar-ubl',
      en: 'pdf-to-ubl',
      de: 'pdf-zu-ubl',
      fr: 'pdf-vers-ubl',
    },
    category: 'converter',
    component: 'PdfToUblConverter',
    clientSide: true,
  },
  {
    id: 'ubl-to-pdf',
    slugs: {
      nl: 'ubl-naar-pdf',
      en: 'ubl-to-pdf',
      de: 'ubl-zu-pdf',
      fr: 'ubl-vers-pdf',
    },
    category: 'converter',
    component: 'UblToPdfConverter',
    clientSide: true,
  },
  {
    id: 'invoice-creator',
    slugs: {
      nl: 'factuur-maken',
      en: 'invoice-creator',
      de: 'rechnung-erstellen',
      fr: 'creer-facture',
    },
    category: 'generator',
    component: 'InvoiceCreator',
    clientSide: true,
  },
  {
    id: 'vat-calculator',
    slugs: {
      nl: 'btw-calculator',
      en: 'vat-calculator',
      de: 'mwst-rechner',
      fr: 'calculateur-tva',
    },
    category: 'calculator',
    component: 'VatCalculator',
    clientSide: true,
  },
  {
    id: 'ubl-validator',
    slugs: {
      nl: 'ubl-validator',
      en: 'ubl-validator',
      de: 'ubl-validator',
      fr: 'ubl-validator',
    },
    category: 'validator',
    component: 'UblValidator',
    clientSide: true,
  },
  {
    id: 'iban-validator',
    slugs: {
      nl: 'iban-validator',
      en: 'iban-validator',
      de: 'iban-validator',
      fr: 'iban-validator',
    },
    category: 'validator',
    component: 'IbanValidator',
    clientSide: true,
  },
  {
    id: 'credit-note-creator',
    slugs: {
      nl: 'creditnota-maken',
      en: 'credit-note-creator',
      de: 'gutschrift-erstellen',
      fr: 'creer-avoir',
    },
    category: 'generator',
    component: 'CreditNoteCreator',
    clientSide: true,
  },
  {
    id: 'bol-calculator',
    slugs: {
      nl: 'bol-kosten-calculator',
      en: 'bol-cost-calculator',
      de: 'bol-kosten-rechner',
      fr: 'calculateur-couts-bol',
    },
    category: 'calculator',
    component: 'BolCalculator',
    clientSide: true,
  },
  {
    id: 'amazon-calculator',
    slugs: {
      nl: 'amazon-fba-kosten-calculator',
      en: 'amazon-fba-cost-calculator',
      de: 'amazon-fba-kosten-rechner',
      fr: 'calculateur-couts-amazon-fba',
    },
    category: 'calculator',
    component: 'AmazonCalculator',
    clientSide: true,
  },
  {
    id: 'etsy-calculator',
    slugs: {
      nl: 'etsy-kosten-calculator',
      en: 'etsy-cost-calculator',
      de: 'etsy-kosten-rechner',
      fr: 'calculateur-couts-etsy',
    },
    category: 'calculator',
    component: 'EtsyCalculator',
    clientSide: true,
  },
  {
    id: 'shopify-calculator',
    slugs: {
      nl: 'shopify-kosten-calculator',
      en: 'shopify-cost-calculator',
      de: 'shopify-kosten-rechner',
      fr: 'calculateur-couts-shopify',
    },
    category: 'calculator',
    component: 'ShopifyCalculator',
    clientSide: true,
  },
  {
    id: 'oss-report',
    slugs: {
      nl: 'oss-aangifte',
      en: 'oss-report',
      de: 'oss-meldung',
      fr: 'declaration-oss',
    },
    category: 'calculator',
    component: 'OssReport',
    clientSide: true,
  },
];

export function getToolBySlug(slug: string, locale: Locale): ToolConfig | undefined {
  return tools.find(tool => tool.slugs[locale] === slug);
}

export function getToolById(id: string): ToolConfig | undefined {
  return tools.find(tool => tool.id === id);
}

export function getAllToolSlugs(): { lang: Locale; slug: string }[] {
  const result: { lang: Locale; slug: string }[] = [];
  const locales: Locale[] = ['nl', 'en', 'de', 'fr'];

  for (const tool of tools) {
    for (const locale of locales) {
      result.push({ lang: locale, slug: tool.slugs[locale] });
    }
  }

  return result;
}

export function getToolTranslatedSlug(toolId: string, locale: Locale): string | undefined {
  const tool = getToolById(toolId);
  return tool?.slugs[locale];
}

// Tool slug translations for hreflang
export const toolTranslations: Record<string, Record<Locale, string>> = tools.reduce((acc, tool) => {
  // Add entry for each slug variation pointing to all translations
  const locales: Locale[] = ['nl', 'en', 'de', 'fr'];
  for (const locale of locales) {
    acc[tool.slugs[locale]] = tool.slugs;
  }
  return acc;
}, {} as Record<string, Record<Locale, string>>);
